import styles from './styles.module.scss';
import { PartLookUpResult } from 'api/generated';
import cn from 'classnames';
import { useAppStore } from 'features/settings/contexts/settings.context';
import { FC, useState } from 'react';
import { Button } from 'react-bootstrap';

type Props = {
  parts: PartLookUpResult[];
  onClose: () => void;
  onSelectPart?: (part: PartLookUpResult) => void;
};

export const ModalBody: FC<Props> = ({ parts, onClose, onSelectPart }) => {
  const [selectedId, setSelectedId] = useState<string | null>(parts.find((x) => x.isMainPart)?.info?.partId ?? null);

  const {
    state: { branches }
  } = useAppStore();

  const getBrandCode = (part: PartLookUpResult) => {
    if (part?.availability?.length) {
      return part.availability[0].brandCode;
    }

    return part?.info?.brandCode;
  };
  return (
    <div className="mt-4">
      {parts.map((x) => {
        const isSelected = x.info?.partId === selectedId;

        return (
          <div
            key={x.info?.partId}
            onClick={() => setSelectedId(x.info?.partId ?? null)}
            className={cn('px-2 py-6 cursor-pointer d-flex align-items-start mb-3', {
              [styles.selectedRow]: isSelected
            })}
          >
            <div className="me-4 d-flex justify-content-center align-self-center">
              {isSelected ? (
                <svg width="26" height="27" viewBox="0 0 26 27" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="13" cy="13.5" r="13" fill="#3E97FF" />
                  <circle cx="13.0036" cy="13.5036" r="6.93333" fill="white" />
                </svg>
              ) : (
                <svg width="26" height="27" viewBox="0 0 26 27" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="13" cy="13.5" r="13" fill="#F1F1F2" />
                </svg>
              )}
            </div>
            <div className="flex-grow-1">
              <div className="d-flex align-items-center mb-2">
                <div className="fw-bold flex-shrink-0 me-2">
                  {getBrandCode(x)}
                  {x.info?.partCode}
                </div>
                <div className="text-muted flex-grow-1 text-center">{x.info?.description}</div>
              </div>
              <div className="d-flex flex-wrap gap-2">
                {x.availability?.map((y) => (
                  <span
                    className={cn('badge px-3 py-1 rounded-pill d-flex align-items-center', {
                      [styles.badgeGreen]: y.availableStock && y.availableStock > 0,
                      [styles.badgeRed]: !y.availableStock || y.availableStock < 0
                    })}
                    key={y.branch}
                  >
                    <span className="me-1">
                      {y.availableStock && y.availableStock > 0 ? (
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            opacity="0.3"
                            d="M8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16Z"
                            fill="white"
                          />
                          <path
                            d="M7.76659 10.9681L12.5666 6.16815C12.6412 6.09356 12.7003 6.00501 12.7407 5.90755C12.7811 5.81009 12.8019 5.70564 12.8019 5.60015C12.8019 5.49466 12.7811 5.39021 12.7407 5.29275C12.7003 5.19529 12.6412 5.10674 12.5666 5.03215C12.492 4.95756 12.4034 4.89839 12.306 4.85802C12.2085 4.81765 12.1041 4.79687 11.9986 4.79688C11.8931 4.79688 11.7886 4.81765 11.6912 4.85802C11.5937 4.89839 11.5052 4.95756 11.4306 5.03215L7.19859 9.27215L4.56659 6.63215C4.41594 6.48151 4.21163 6.39688 3.99859 6.39688C3.78554 6.39688 3.58123 6.48151 3.43059 6.63215C3.27994 6.78279 3.19531 6.98711 3.19531 7.20015C3.19531 7.41319 3.27994 7.61751 3.43059 7.76815L6.63059 10.9681C6.70496 11.0431 6.79344 11.1026 6.89092 11.1433C6.98841 11.1839 7.09298 11.2048 7.19859 11.2048C7.3042 11.2048 7.40876 11.1839 7.50625 11.1433C7.60373 11.1026 7.69222 11.0431 7.76659 10.9681Z"
                            fill="white"
                          />
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            opacity="0.3"
                            d="M8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16Z"
                            fill="white"
                          />
                          <path
                            d="M9.12763 7.99976L11.3916 5.73576C11.4754 5.66404 11.5434 5.57579 11.5914 5.47654C11.6394 5.37728 11.6664 5.26918 11.6707 5.159C11.6749 5.04883 11.6564 4.93896 11.6161 4.8363C11.5759 4.73364 11.5149 4.6404 11.437 4.56244C11.359 4.48448 11.2658 4.42347 11.1631 4.38325C11.0604 4.34303 10.9506 4.32447 10.8404 4.32872C10.7302 4.33298 10.6221 4.35996 10.5229 4.40798C10.4236 4.456 10.3354 4.52401 10.2636 4.60776L7.99963 6.87176L5.73563 4.60776C5.58259 4.4767 5.38573 4.40821 5.18439 4.41599C4.98305 4.42377 4.79206 4.50723 4.64958 4.64971C4.50711 4.79218 4.42364 4.98318 4.41586 5.18452C4.40809 5.38586 4.47657 5.58272 4.60763 5.73576L6.87163 7.99976L4.60763 10.2638C4.45863 10.4137 4.375 10.6164 4.375 10.8278C4.375 11.0391 4.45863 11.2419 4.60763 11.3918C4.75752 11.5408 4.96029 11.6244 5.17163 11.6244C5.38298 11.6244 5.58574 11.5408 5.73563 11.3918L7.99963 9.12776L10.2636 11.3918C10.4135 11.5408 10.6163 11.6244 10.8276 11.6244C11.039 11.6244 11.2417 11.5408 11.3916 11.3918C11.5406 11.2419 11.6243 11.0391 11.6243 10.8278C11.6243 10.6164 11.5406 10.4137 11.3916 10.2638L9.12763 7.99976Z"
                            fill="white"
                          />
                        </svg>
                      )}
                    </span>
                    {branches.find((x) => x.value === y.branch)?.label}{' '}
                    {y.availableStock && y.availableStock > 0 ? y.availableStock : 0}
                  </span>
                ))}
              </div>
            </div>
          </div>
        );
      })}
      <div className="mt-8">
        <Button
          variant="danger"
          onClick={() => {
            const part = parts.find((x) => x.info?.partId === selectedId);
            if (onSelectPart && part) {
              onSelectPart?.(part);
              onClose();
            }
          }}
          disabled={!selectedId}
        >
          <div className="mx-2">Select</div>
        </Button>
      </div>
    </div>
  );
};
